import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { createClient } from "@/lib/supabase/client";

import {
  buildChatAppLaunchPayload,
  CHAT_APP_EMBED_READY_EVENT,
  CHAT_APP_EMBED_SESSION_OVERRIDE_STORAGE_KEY,
  CHAT_APP_EMBED_TIMEOUT_MS,
  CHAT_APP_EMBED_URL_OVERRIDE_STORAGE_KEY,
  CHAT_APP_PLATFORM_AUTH_ACK_EVENT,
  CHAT_APP_PLATFORM_CONNECTION_STATUS_EVENT,
  CHAT_APP_PLATFORM_DISCONNECT_EVENT,
  CHAT_APP_PLATFORM_MEDIA_STATE_EVENT,
  CHAT_APP_PLATFORM_TASK_EVENT,
  CHAT_APP_WORLD_TASKS_CHANGED_EVENT,
  emitChatAppEmbedTrace,
  getChatAppEmbedAuthError,
  getChatAppAgentSignature,
  getDefaultChatAppConnectionStatus,
  normalizeChatAppConnectionStatusPayload,
  resolveCompletedPlatformTask,
  resolveFailedPlatformTask,
  resolveChatAppSessionInfo,
  resolveChatAppEmbedUrl,
} from "../lib/chatAppEmbed";
import useWorldStore from "../store/worldStore";
import {
  postWorldTaskEvent,
} from "../../../lib/backend/world";

let chatAppEmbedFrameInstanceCounter = 0;

export default function ChatAppEmbedFrame({
  agent,
  title = "Chat App Call",
  launchEnabled = true,
  mediaState = null,
  onConnectionStatusChange,
  prewarmOnly = false,
  uiMode = "call",
}) {
  const instanceIdRef = useRef(++chatAppEmbedFrameInstanceCounter);
  const iframeRef = useRef(null);
  const targetOriginRef = useRef(null);
  const launchPayloadRef = useRef(null);
  const retryIntervalRef = useRef(null);
  const iframeSrcRef = useRef(null);
  const agentIdRef = useRef(null);
  const connectionStatusRef = useRef(getDefaultChatAppConnectionStatus());
  const launchedAgentSignatureRef = useRef(null);
  const launchEnabledRef = useRef(launchEnabled);
  const launchSequenceRef = useRef(0);
  const sessionUserIdRef = useRef(null);
  const postedMediaStateSignatureRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [iframeSrc, setIframeSrc] = useState(null);
  const [iframeInstanceKey, setIframeInstanceKey] = useState(0);
  const [launchToken, setLaunchToken] = useState(null);
  const [sessionResolved, setSessionResolved] = useState(false);
  const [sessionInfo, setSessionInfo] = useState({ accessToken: null, userId: null });
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const setOptimisticAgentToolUse = useWorldStore((s) => s.setOptimisticAgentToolUse);
  const clearOptimisticAgentToolUse = useWorldStore((s) => s.clearOptimisticAgentToolUse);
  const setOptimisticTaskClosure = useWorldStore((s) => s.setOptimisticTaskClosure);
  const clearOptimisticTaskClosure = useWorldStore((s) => s.clearOptimisticTaskClosure);
  const openResultModal = useWorldStore((s) => s.openResultModal);
  const agentId = agent?.chatAppCharacterId ?? null;
  const agentName = agent?.characterConfig?.name ?? agent?.name ?? null;
  const agentSignature = useMemo(() => getChatAppAgentSignature(agent), [agent]);
  const storeAgentId = agent?.id?.trim?.() || agent?.openclawAgentId?.trim?.() || null;
  const mediaStateSignature = useMemo(
    () => (mediaState ? JSON.stringify(mediaState) : null),
    [mediaState]
  );
  const shouldPostInitialMediaState = useMemo(() => {
    if (!mediaState) {
      return false;
    }
    if (mediaState.uiMode !== "call") {
      return true;
    }

    return Boolean(
      mediaState.minimized
        || !mediaState.cameraEnabled
        || !mediaState.micEnabled
        || !mediaState.speakerEnabled
        || mediaState.screenShareEnabled
    );
  }, [mediaState]);

  const updateConnectionStatus = useCallback(
    (nextStatus) => {
      const normalizedStatus = normalizeChatAppConnectionStatusPayload(nextStatus)
      connectionStatusRef.current = normalizedStatus
      onConnectionStatusChange?.(normalizedStatus)
    },
    [onConnectionStatusChange]
  )

  useEffect(() => {
    onConnectionStatusChange?.(connectionStatusRef.current)
  }, [onConnectionStatusChange])

  const rotateLaunch = useCallback(
    (reason) => {
      if (launchSequenceRef.current > 0) {
        setIframeInstanceKey((current) => current + 1);
      }
      launchSequenceRef.current += 1;
      const nextLaunchToken = `launch-${Date.now()}-${launchSequenceRef.current}`;
      launchedAgentSignatureRef.current = agentSignature;
      setLaunchToken(nextLaunchToken);
      emitChatAppEmbedTrace("ChatAppEmbedFrame", "launch-token-rotated", {
        agentId,
        instanceId: instanceIdRef.current,
        launchToken: nextLaunchToken,
        reason,
      });
    },
    [agentId, agentSignature]
  );

  useEffect(() => {
    agentIdRef.current = agentId;
    sessionUserIdRef.current = sessionInfo.userId;
  }, [agentId, sessionInfo.userId]);

  const embedUrl = useMemo(() => {
    try {
      const resolvedUrl = resolveChatAppEmbedUrl({
        configuredUrl: process.env.NEXT_PUBLIC_CHAT_APP_EMBED_URL,
        overrideUrl:
          typeof window !== "undefined"
            ? window.localStorage.getItem(CHAT_APP_EMBED_URL_OVERRIDE_STORAGE_KEY)
            : null,
      });
      if (!resolvedUrl) {
        return null;
      }

      const url = new URL(resolvedUrl, window.location.origin);
      if (uiMode) {
        url.searchParams.set("uiMode", uiMode);
      }
      return url.toString().replace(/\/$/, "");
    } catch {
      return null;
    }
  }, [uiMode]);

  useEffect(() => {
    const instanceId = instanceIdRef.current;
    emitChatAppEmbedTrace("ChatAppEmbedFrame", "mount", {
      instanceId,
      prewarmOnly,
    });

    return () => {
      if (storeAgentId) {
        clearOptimisticAgentToolUse(storeAgentId);
        clearOptimisticTaskClosure(storeAgentId);
      }
      emitChatAppEmbedTrace("ChatAppEmbedFrame", "unmount", {
        instanceId,
      });
    };
  }, [clearOptimisticAgentToolUse, clearOptimisticTaskClosure, prewarmOnly, storeAgentId]);

  useEffect(() => {
    launchPayloadRef.current =
      agentId && agentName
        ? buildChatAppLaunchPayload({
            accessToken: sessionInfo.accessToken,
            agent: { id: agentId, name: agentName },
            launchToken,
            openclaw: {
              connectionId: agent?.openclawConnectionId ?? null,
              agentId: agent?.openclawAgentId ?? null,
              agentName: agent?.openclawAgentName ?? null,
            },
            templateBuildId: agent?.templateBuildId ?? null,
            templateCharacterProfile:
              agent?.chatAppCharacterProfile ?? agent?.templateCharacterProfile ?? null,
            userId: sessionInfo.userId,
          })
        : null;
  }, [
    agent,
    agentId,
    agentName,
    launchToken,
    sessionInfo.accessToken,
    sessionInfo.userId,
  ]);

  const postLaunchPayload = useCallback(() => {
    if (!launchEnabled || !launchPayloadRef.current || !targetOriginRef.current) return;
    emitChatAppEmbedTrace("ChatAppEmbedFrame", "post-launch-payload", {
      agentId,
      instanceId: instanceIdRef.current,
      launchToken,
      userId: sessionInfo.userId,
    });
    iframeRef.current?.contentWindow?.postMessage(
      launchPayloadRef.current,
      targetOriginRef.current
    );
  }, [agentId, launchEnabled, launchToken, sessionInfo.userId]);

  const clearRetryInterval = useCallback(() => {
    if (retryIntervalRef.current) {
      window.clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
  }, []);

  const handleReconnect = useCallback(() => {
    if (!launchEnabled) return;

    clearRetryInterval();
    iframeSrcRef.current = null;
    targetOriginRef.current = null;
    postedMediaStateSignatureRef.current = null;
    updateConnectionStatus(getDefaultChatAppConnectionStatus())
    setError(null);
    setReady(false);
    setIframeSrc(null);
    setSessionResolved(false);
    setReconnectAttempt((current) => current + 1);
    rotateLaunch("manual-reconnect");
  }, [clearRetryInterval, launchEnabled, rotateLaunch, updateConnectionStatus]);

  const postDisconnectSignal = useCallback((options = {}) => {
    const { updateOptimisticState = true } = options;
    if (!targetOriginRef.current) return;
    if (updateOptimisticState && storeAgentId) {
      clearOptimisticAgentToolUse(storeAgentId);
      setOptimisticTaskClosure(storeAgentId);
    }
    emitChatAppEmbedTrace("ChatAppEmbedFrame", "post-disconnect-signal", {
      instanceId: instanceIdRef.current,
      userId: sessionInfo.userId,
    });
    iframeRef.current?.contentWindow?.postMessage(
      { type: CHAT_APP_PLATFORM_DISCONNECT_EVENT },
      targetOriginRef.current
    );
  }, [clearOptimisticAgentToolUse, sessionInfo.userId, setOptimisticTaskClosure, storeAgentId]);

  const postMediaState = useCallback(() => {
    if (!targetOriginRef.current || !mediaState) return;
    emitChatAppEmbedTrace("ChatAppEmbedFrame", "post-media-state", {
      agentId,
      instanceId: instanceIdRef.current,
      mediaState,
    });
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: CHAT_APP_PLATFORM_MEDIA_STATE_EVENT,
        payload: mediaState,
      },
      targetOriginRef.current
    );
  }, [agentId, mediaState]);

  const handlePlatformTaskEvent = useCallback(async (taskPayload) => {
    const taskSyncAgentId = agent?.openclawAgentId?.trim?.() || storeAgentId;
    if (!taskSyncAgentId || !storeAgentId || !taskPayload || typeof taskPayload !== 'object') {
      return;
    }

    if (taskPayload.local_only) {
      if (taskPayload.event === 'tool_use') {
        clearOptimisticTaskClosure(storeAgentId);
        setOptimisticAgentToolUse(storeAgentId, {
          ...(taskPayload.tool_use && typeof taskPayload.tool_use === 'object'
            ? taskPayload.tool_use
            : {}),
        });
      } else if (taskPayload.event === 'clear_tool_use') {
        clearOptimisticAgentToolUse(storeAgentId);
        setOptimisticTaskClosure(storeAgentId);
      }
      return;
    }

    try {
      const snapshot = await postWorldTaskEvent(taskSyncAgentId, taskPayload);
      clearOptimisticAgentToolUse(storeAgentId);
      clearOptimisticTaskClosure(storeAgentId);
      useWorldStore
        .getState()
        .applyAgentTaskSnapshot(storeAgentId, snapshot);
      const completedTask = resolveCompletedPlatformTask({
        agentId: storeAgentId,
        taskPayload,
        snapshot,
      });
      if (completedTask) {
        openResultModal(completedTask);
      }
      const failedTask = resolveFailedPlatformTask({
        agentId: storeAgentId,
        taskPayload,
        snapshot,
      });
      if (failedTask) {
        openResultModal(failedTask);
      }
      window.dispatchEvent(
        new CustomEvent(CHAT_APP_WORLD_TASKS_CHANGED_EVENT, {
          detail: {
            agentId: storeAgentId,
            tasks: snapshot?.tasks ?? null,
            world: snapshot?.world ?? null,
            life_sim: snapshot?.life_sim ?? null,
          },
        })
      );
    } catch (error) {
      clearOptimisticAgentToolUse(storeAgentId);
      clearOptimisticTaskClosure(storeAgentId);
      const failedTask = resolveFailedPlatformTask({
        agentId: storeAgentId,
        taskPayload,
        snapshot: null,
      });
      if (failedTask) {
        openResultModal(failedTask);
      }
      emitChatAppEmbedTrace("ChatAppEmbedFrame", "platform-task-event-failed", {
        agentId: taskSyncAgentId,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [agent?.openclawAgentId, clearOptimisticAgentToolUse, clearOptimisticTaskClosure, openResultModal, setOptimisticAgentToolUse, setOptimisticTaskClosure, storeAgentId]);

  useEffect(() => {
    const supabase = createClient();
    const overrideValue =
      typeof window !== "undefined"
        ? window.localStorage.getItem(CHAT_APP_EMBED_SESSION_OVERRIDE_STORAGE_KEY)
        : null;
    let isMounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!isMounted) return;
        emitChatAppEmbedTrace("ChatAppEmbedFrame", "session-resolved", {
          hasAccessToken: Boolean(data.session?.access_token || overrideValue),
          userId: data.session?.user?.id ?? null,
        });
        setSessionInfo({
          ...resolveChatAppSessionInfo({
            session: data.session ?? null,
            overrideValue,
          }),
        });
        setSessionResolved(true);
      })
      .catch(() => {
        if (!isMounted) return;
        emitChatAppEmbedTrace("ChatAppEmbedFrame", "session-resolved-fallback", {
          hasOverride: Boolean(overrideValue),
        });
        setSessionInfo(
          resolveChatAppSessionInfo({
            session: null,
            overrideValue,
          })
        );
        setSessionResolved(true);
      });

    return () => {
      isMounted = false;
    };
  }, [reconnectAttempt]);

  const iframeName = useMemo(() => {
    if (!sessionInfo.accessToken || !sessionInfo.userId) {
      return "";
    }

    return JSON.stringify({
      accessToken: sessionInfo.accessToken,
      userId: sessionInfo.userId,
    });
  }, [sessionInfo.accessToken, sessionInfo.userId]);

  useEffect(() => {
    emitChatAppEmbedTrace("ChatAppEmbedFrame", "iframe-name-updated", {
      hasAgent: Boolean(agentId && agentName),
      instanceId: instanceIdRef.current,
      nameLength: iframeName.length,
      prewarmOnly,
      sessionOnlyBootstrap: true,
    });
  }, [agentId, agentName, iframeName, prewarmOnly]);

  useLayoutEffect(() => {
    if (!embedUrl) {
      iframeSrcRef.current = null;
      updateConnectionStatus(getDefaultChatAppConnectionStatus())
      setIframeSrc(null);
      setError("Chat app URL not configured.");
      return;
    }

    if (!prewarmOnly && (!agentId || !agentName)) {
      iframeSrcRef.current = null;
      updateConnectionStatus(getDefaultChatAppConnectionStatus())
      setIframeSrc(null);
      setError("Chat unavailable: this world agent is missing a mapped chat-app character.");
      return;
    }

    const authError = sessionResolved ? getChatAppEmbedAuthError(sessionInfo) : null;
    if (authError) {
      setReady(false);
      updateConnectionStatus(getDefaultChatAppConnectionStatus())
      iframeSrcRef.current = null;
      setIframeSrc(null);
      setError(authError);
      return;
    }

    setError(null);
    targetOriginRef.current = new URL(embedUrl).origin;
    const shouldLoadIframe = iframeSrcRef.current !== embedUrl;
    if (shouldLoadIframe) {
      iframeSrcRef.current = embedUrl;
      updateConnectionStatus(getDefaultChatAppConnectionStatus())
      setReady(false);
      setIframeSrc(embedUrl);
      emitChatAppEmbedTrace("ChatAppEmbedFrame", "iframe-src-set", {
        agentId,
        embedUrl,
        instanceId: instanceIdRef.current,
      });
    } else {
      emitChatAppEmbedTrace("ChatAppEmbedFrame", "iframe-src-reused", {
        agentId,
        embedUrl,
        instanceId: instanceIdRef.current,
        ready,
      });
    }

    clearRetryInterval();
    const timeoutId =
      shouldLoadIframe || !ready
        ? window.setTimeout(() => {
            clearRetryInterval();
            setError("Chat app did not finish loading.");
          }, CHAT_APP_EMBED_TIMEOUT_MS)
        : null;

    const handleMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== targetOriginRef.current) return;
      if (event.data?.type === CHAT_APP_EMBED_READY_EVENT) {
        emitChatAppEmbedTrace("ChatAppEmbedFrame", "embed-ready-received", {
          agentId: agentIdRef.current,
          instanceId: instanceIdRef.current,
        });
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        clearRetryInterval();
        setReady(true);
        if (launchEnabled) {
          postLaunchPayload();
        }
        return;
      }

      if (event.data?.type === CHAT_APP_PLATFORM_TASK_EVENT) {
        void handlePlatformTaskEvent(event.data?.payload);
        return;
      }

      if (event.data?.type === CHAT_APP_PLATFORM_CONNECTION_STATUS_EVENT) {
        updateConnectionStatus(event.data?.payload)
        return
      }

      if (event.data?.type !== CHAT_APP_PLATFORM_AUTH_ACK_EVENT) return;

      const ackUserId = event.data?.payload?.userId?.trim?.() ?? null;
      if (sessionUserIdRef.current && ackUserId && ackUserId !== sessionUserIdRef.current) {
        return;
      }

      emitChatAppEmbedTrace("ChatAppEmbedFrame", "auth-ack-received", {
        ackUserId,
        agentId: agentIdRef.current,
        instanceId: instanceIdRef.current,
      });
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      clearRetryInterval();
      setReady(true);
    };

    window.addEventListener("message", handleMessage);
    const hasPayload = Boolean(launchPayloadRef.current);
    if (hasPayload && launchEnabled) {
      retryIntervalRef.current = window.setInterval(() => {
        postLaunchPayload();
      }, 300);
    }

    return () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      clearRetryInterval();
      updateConnectionStatus(getDefaultChatAppConnectionStatus())
      window.removeEventListener("message", handleMessage);
      targetOriginRef.current = null;
    };
  }, [
    agentId,
    agentName,
    clearRetryInterval,
    embedUrl,
    handlePlatformTaskEvent,
    launchEnabled,
    postLaunchPayload,
    prewarmOnly,
    ready,
    sessionInfo,
    sessionResolved,
    updateConnectionStatus,
  ]);

  useEffect(() => {
    if (!launchEnabled || !agentId || !agentName) {
      return;
    }
    if (launchToken) {
      return;
    }

    rotateLaunch("launch-enabled-initial");
  }, [agentId, agentName, launchEnabled, launchToken, rotateLaunch]);

  useEffect(() => {
    const wasLaunchEnabled = launchEnabledRef.current;
    launchEnabledRef.current = launchEnabled;
    if (!wasLaunchEnabled && launchEnabled) {
      rotateLaunch("window-opened");
    }
    if (wasLaunchEnabled && !launchEnabled) {
      launchedAgentSignatureRef.current = null;
      setLaunchToken(null);
      updateConnectionStatus(getDefaultChatAppConnectionStatus())
      postDisconnectSignal();
    }
  }, [launchEnabled, postDisconnectSignal, rotateLaunch, updateConnectionStatus]);

  useEffect(() => {
    if (!launchEnabled) {
      return;
    }
    if (!launchedAgentSignatureRef.current) {
      launchedAgentSignatureRef.current = agentSignature;
      return;
    }
    if (launchedAgentSignatureRef.current === agentSignature) {
      return;
    }

    postDisconnectSignal({ updateOptimisticState: false });
    const relaunchTimer = window.setTimeout(() => {
      rotateLaunch("agent-changed");
    }, 150);

    return () => {
      window.clearTimeout(relaunchTimer);
    };
  }, [agentSignature, launchEnabled, postDisconnectSignal, rotateLaunch]);

  useEffect(() => {
    if (ready && launchEnabled && !getChatAppEmbedAuthError(sessionInfo)) {
      postLaunchPayload();
    }
  }, [agentSignature, launchEnabled, postLaunchPayload, ready, sessionInfo]);

  useEffect(() => {
    if (!mediaStateSignature) {
      postedMediaStateSignatureRef.current = null;
      return;
    }
    if (!ready || getChatAppEmbedAuthError(sessionInfo)) {
      return;
    }
    if (postedMediaStateSignatureRef.current === null) {
      postedMediaStateSignatureRef.current = mediaStateSignature;
      if (shouldPostInitialMediaState) {
        postMediaState();
      }
      return;
    }
    if (postedMediaStateSignatureRef.current === mediaStateSignature) {
      return;
    }

    postedMediaStateSignatureRef.current = mediaStateSignature;
    postMediaState();
  }, [mediaStateSignature, postMediaState, ready, sessionInfo, shouldPostInitialMediaState]);

  const canReconnect =
    launchEnabled
    && Boolean(embedUrl && agentId && agentName);

  if (error) {
    return (
      <InlineState
        tone="error"
        title="Chat failed to launch"
        actionLabel={canReconnect ? "Reconnect" : undefined}
        onAction={canReconnect ? handleReconnect : undefined}
      >
        {error}
      </InlineState>
    );
  }

  if (!embedUrl) {
    return <InlineState tone="error">Chat app URL not configured.</InlineState>;
  }

  return (
    <div style={containerStyle}>
      {!sessionResolved ? (
        <InlineState tone="loading">Checking character-platform session...</InlineState>
      ) : !ready ? (
        <InlineState tone="loading">
          Loading chat for {agent?.name ?? "agent"}...
        </InlineState>
      ) : null}
      {iframeSrc ? (
        <iframe
          key={iframeInstanceKey}
          ref={iframeRef}
          title={title}
          src={iframeSrc}
          name={iframeName}
          allow="microphone; camera; autoplay; display-capture"
          onLoad={() => {
            if (getChatAppEmbedAuthError(sessionInfo)) return;
            emitChatAppEmbedTrace("ChatAppEmbedFrame", "iframe-load", {
              agentId,
              instanceId: instanceIdRef.current,
            });
            setReady(true);
            if (launchEnabled) {
              postLaunchPayload();
            }
          }}
          style={iframeStyle}
        />
      ) : null}
    </div>
  );
}

function InlineState({ children, tone, title = null, actionLabel, onAction }) {
  const palette =
    tone === "error"
      ? {
          border: "rgba(248, 113, 113, 0.35)",
          color: "rgba(254, 226, 226, 0.95)",
          background: "rgba(127, 29, 29, 0.42)",
        }
      : {
          border: "rgba(255,255,255,0.12)",
          color: "rgba(255,255,255,0.72)",
          background: "rgba(8,10,16,0.78)",
        };

  return (
    <div
      style={{
        position: "absolute",
        inset: 24,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        borderRadius: 12,
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.color,
        fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
        fontSize: 11,
        letterSpacing: "0.04em",
        zIndex: 2,
        padding: "16px 20px",
        pointerEvents: onAction ? "auto" : "none",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {title ? (
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </div>
      ) : null}
      <div>{children}</div>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{
            minWidth: 112,
            height: 34,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.18)",
            background: tone === "error" ? "rgba(248, 113, 113, 0.16)" : "rgba(255,255,255,0.08)",
            color: "white",
            cursor: "pointer",
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

const containerStyle = {
  position: "relative",
  width: "100%",
  height: "100%",
  minHeight: 0,
  background: "#05070d",
};

const iframeStyle = {
  display: "block",
  width: "100%",
  height: "100%",
  border: "none",
  background: "#05070d",
};
